import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { apiHandler } from '../api/apihandler';
import { ANNOUNCEMENT_LIST_API } from '../api/constants';

const DISMISSED_KEY = 'dismissedAnnouncementIds';
const ROTATE_MS = 7000;

const getDismissedIds = () => {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
  } catch (e) {
    return [];
  }
};

const addDismissedId = (id) => {
  try {
    const ids = getDismissedIds();
    if (!ids.includes(id)) {
      ids.push(id);
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
    }
  } catch (e) {
    // localStorage unavailable (private mode, quota, ...) - the banner just
    // won't remember the dismissal across reloads, no functional break.
  }
};

const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Slim, dismissible, auto-rotating announcement bar - mounted once in the
// authenticated dashboard shell (layouts/dashboard/default.js) so it shows
// on every page without every screen needing its own wiring. Backed by
// public_announcements via the audience-filtered ANNOUNCEMENT_LIST_API
// (see harmonydaycare-api's AnnouncementController) - never the unrelated
// feed-post "Announcement" screens elsewhere in this app.
const AnnouncementBanner = () => {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dismissedIds, setDismissedIds] = useState(() => getDismissedIds());
  const [detailsAnnouncement, setDetailsAnnouncement] = useState(null);
  const rotateTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiHandler(ANNOUNCEMENT_LIST_API, 'POST', {});
        if (cancelled) return;
        const list = res?.data?.data?.announcements;
        setAnnouncements(Array.isArray(list) ? list : []);
      } catch (err) {
        // Best-effort - a failed fetch just means no banner this visit.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visibleAnnouncements = useMemo(
    () => announcements.filter((a) => !dismissedIds.includes(a.id)),
    [announcements, dismissedIds]
  );

  useEffect(() => {
    if (activeIndex >= visibleAnnouncements.length) {
      setActiveIndex(0);
    }
  }, [visibleAnnouncements.length, activeIndex]);

  useEffect(() => {
    if (visibleAnnouncements.length <= 1) {
      return undefined;
    }
    rotateTimerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % visibleAnnouncements.length);
    }, ROTATE_MS);
    return () => clearInterval(rotateTimerRef.current);
  }, [visibleAnnouncements.length]);

  if (visibleAnnouncements.length === 0) {
    return null;
  }

  // Dismissing a non-last announcement shrinks this list in the same
  // render as the dismiss - the effect above that resets an out-of-range
  // activeIndex only runs on the NEXT render, so this render still needs a
  // safe fallback or `current` below is undefined and crashes the banner
  // (which looks like "close doesn't work" - the whole thing errors out).
  const current = visibleAnnouncements[activeIndex] ?? visibleAnnouncements[0];

  const handleDismiss = (e) => {
    e.stopPropagation();
    addDismissedId(current.id);
    setDismissedIds((prev) => [...prev, current.id]);
  };

  const goTo = (e, delta) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev + delta + visibleAnnouncements.length) % visibleAnnouncements.length);
  };

  return (
    <>
      <style>{`
        .announcement-banner {
          background: linear-gradient(90deg, #6f42c1 0%, #8a5cf6 100%);
          color: #fff;
          display: flex;
          align-items: center;
          padding: 10px 20px;
          gap: 12px;
          cursor: pointer;
        }
        .announcement-banner .announcement-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .announcement-banner .announcement-text {
          flex: 1 1 auto;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 14px;
          font-weight: 500;
        }
        .announcement-banner .announcement-badge {
          background: rgba(255,255,255,0.25);
          border-radius: 20px;
          padding: 2px 10px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          flex-shrink: 0;
        }
        .announcement-banner .announcement-nav-btn {
          background: transparent;
          border: none;
          color: #fff;
          opacity: 0.85;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2px;
          flex-shrink: 0;
        }
        .announcement-banner .announcement-nav-btn:hover {
          opacity: 1;
        }
        .announcement-banner .announcement-dots {
          display: flex;
          gap: 5px;
          flex-shrink: 0;
        }
        .announcement-banner .announcement-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: rgba(255,255,255,0.4);
        }
        .announcement-banner .announcement-dot.active {
          background: #fff;
        }
      `}</style>
      <div className="announcement-banner" onClick={() => setDetailsAnnouncement(current)}>
        <i className="material-symbols-outlined announcement-icon">campaign</i>
        <span className="announcement-badge">Announcement</span>
        <span className="announcement-text">{current.announcement_details}</span>
        {visibleAnnouncements.length > 1 && (
          <>
            <button className="announcement-nav-btn" onClick={(e) => goTo(e, -1)} aria-label="Previous announcement">
              <i className="material-symbols-outlined">chevron_left</i>
            </button>
            <div className="announcement-dots">
              {visibleAnnouncements.map((a, i) => (
                <span key={a.id} className={`announcement-dot${i === activeIndex ? ' active' : ''}`} />
              ))}
            </div>
            <button className="announcement-nav-btn" onClick={(e) => goTo(e, 1)} aria-label="Next announcement">
              <i className="material-symbols-outlined">chevron_right</i>
            </button>
          </>
        )}
        <button className="announcement-nav-btn" onClick={handleDismiss} aria-label="Dismiss announcement">
          <i className="material-symbols-outlined">close</i>
        </button>
      </div>

      <Modal show={!!detailsAnnouncement} onHide={() => setDetailsAnnouncement(null)} centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <i className="material-symbols-outlined" style={{ color: '#6f42c1' }}>campaign</i>
            Announcement
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {detailsAnnouncement && (
            <>
              <p style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>{detailsAnnouncement.announcement_details}</p>
              {(formatDate(detailsAnnouncement.start_date) || formatDate(detailsAnnouncement.end_date)) && (
                <div className="text-muted small mb-2">
                  {formatDate(detailsAnnouncement.start_date) && <>From {formatDate(detailsAnnouncement.start_date)} </>}
                  {formatDate(detailsAnnouncement.end_date) && <>to {formatDate(detailsAnnouncement.end_date)}</>}
                </div>
              )}
              {detailsAnnouncement.link && detailsAnnouncement.link_text && (
                <a href={detailsAnnouncement.link} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary mt-2">
                  {detailsAnnouncement.link_text}
                </a>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={() => {
              setDetailsAnnouncement(null);
              navigate('/dashboard/app/school-announcements');
            }}
          >
            View all
          </Button>
          <Button variant="secondary" onClick={() => setDetailsAnnouncement(null)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AnnouncementBanner;
